/**
 * Os destinos automáticos — índice do cliente e índice de cada módulo — apontam
 * para a primeira tela que a pessoa PODE ver (`destinos.ts`), nunca para uma tela
 * fixa. Ver o cabeçalho de `destinos.ts` para o círculo que isto evita.
 */
import { Navigate, useParams } from 'react-router'

import { MODULOS, type Modulo } from '@/app/navigation'
import { useFlags } from '@/flags/flags'
import { primeiraAbaVisivel, primeiraTelaVisivel } from '@/permissions/destinos'
import { usePermissoes } from '@/permissions/usePermission'

/** Estado vazio — quando não há tela nenhuma para onde ir. Redirecionar seria o círculo. */
export function SemTelaLiberada({ mensagem }: { mensagem: string }) {
  return (
    <section className="mx-auto max-w-lg py-16 text-center" role="status">
      <i className="ph ph-lock-simple text-iox-navy mb-4 block text-4xl" aria-hidden="true" />
      <h1 className="type-h2 text-iox-navy mb-2">Nenhuma tela liberada</h1>
      <p className="type-body text-navy-600">{mensagem}</p>
    </section>
  )
}

/** O índice de `/cliente/:idCliente`: o primeiro módulo visível, na primeira aba visível. */
export function PrimeiraTelaDoCliente() {
  const { idCliente } = useParams()
  const destino = primeiraTelaVisivel(usePermissoes(), useFlags(), idCliente ?? null)

  if (!destino) {
    return (
      <SemTelaLiberada mensagem="Seu perfil ainda não dá acesso a nenhuma tela. Peça a um administrador da plataforma para revisar os seus grupos." />
    )
  }
  return <Navigate to={destino} replace />
}

/** O índice de um módulo: a primeira aba que a pessoa pode ver, e não a primeira aba. */
export function PrimeiraAbaDoModulo({ id }: { id: Modulo['id'] }) {
  const mapa = usePermissoes()
  const modulo = MODULOS.find((m) => m.id === id)
  const aba = modulo && primeiraAbaVisivel(modulo, mapa)

  if (!aba) {
    return (
      <SemTelaLiberada mensagem="Nenhuma tela deste módulo está liberada para o seu perfil." />
    )
  }
  return <Navigate to={aba.segmento} replace />
}
